import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { AuthShell } from '@/components/portal/AuthShell'
import { PortalField } from '@/components/portal/PortalField'
import { PortalInput } from '@/components/portal/PortalInput'
import { PasswordInput } from '@/components/portal/PasswordInput'
import { authErrorI18nKey, registerWithCognito } from '@/lib/auth'
import { normalizeIdMobile, signUpPasswordSchema } from '@/lib/validators'
import { setSignupPassword, setSignupPhone, setSignupVerifyEmail } from '@/lib/signup-session'

const signUpSchema = z
  .object({
    name: z.string().min(1),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .email()
      .refine((value) => value.length <= 254 && value.split('@')[0].length <= 64, { message: 'emailTooLong' }),
    phone: z.string().refine((value) => normalizeIdMobile(value) !== null, { message: 'invalidPhone' }),
    referrer: z.string().optional(),
    password: signUpPasswordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'passwordMismatch',
    path: ['confirmPassword'],
  })

type SignUpForm = z.infer<typeof signUpSchema>

export function SignUpPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
  })

  async function onSubmit(data: SignUpForm) {
    setError(null)
    try {
      const phone = normalizeIdMobile(data.phone)
      if (!phone) {
        setError(t('auth.invalidPhone'))
        return
      }
      await registerWithCognito({
        email: data.email,
        password: data.password,
        name: data.name,
        phone,
        referrer: data.referrer,
      })
      setSignupVerifyEmail(data.email)
      setSignupPhone(data.phone)
      setSignupPassword(data.password)
      navigate('/verify-email', { state: { email: data.email, phone: data.phone } })
    } catch (err) {
      const key = authErrorI18nKey(err)
      setError(key ? t(key) : t('common.error'))
    }
  }

  return (
    <AuthShell>
      <h1 className="prod-auth__title">{t('auth.signUpTitle')}</h1>
      <p className="prod-auth__subtitle">{t('auth.signUpSubtitle')}</p>
      <form id="sign-up-form" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
        <fieldset className="prod-form-group">
        <legend className="prod-form-group__title">{t('auth.groupPersonal')}</legend>
        <PortalField label={t('auth.name')} required error={errors.name ? t('validation.required') : undefined}>
          <PortalInput
            placeholder={t('auth.namePlaceholder')}
            autoComplete="name"
            {...register('name')}
          />
        </PortalField>
        <PortalField label={t('auth.email')} required error={errors.email ? t(errors.email.message === 'emailTooLong' ? 'validation.emailTooLong' : 'validation.invalidEmail') : undefined}>
          <PortalInput
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            autoComplete="email"
            maxLength={254}
            {...register('email', {
              onChange: () => setError(null),
            })}
          />
        </PortalField>
        <PortalField label={t('auth.phone')} required error={errors.phone ? t('auth.invalidPhone') : undefined}>
          <div className="prod-phone-row">
            <div className="prod-phone-row__code">🇮🇩 (+62)</div>
            <PortalInput
              className="prod-phone-row__input"
              placeholder={t('auth.phonePlaceholder')}
              autoComplete="tel-national"
              {...register('phone')}
            />
          </div>
        </PortalField>
        <PortalField label={t('auth.referrer')}>
          <PortalInput autoComplete="off" {...register('referrer')} />
        </PortalField>
        </fieldset>
        <fieldset className="prod-form-group">
        <legend className="prod-form-group__title">{t('auth.groupSecurity')}</legend>
        <PortalField
          label={t('auth.password')}
          required
          error={errors.password ? t('validation.passwordPolicy') : undefined}
        >
          <PasswordInput
            placeholder={t('auth.passwordPlaceholder')}
            autoComplete="new-password"
            value={password}
            onChange={(v) => {
              setPassword(v)
              setValue('password', v, { shouldValidate: true })
            }}
            showCriteria
          />
        </PortalField>
        <PortalField label={t('auth.confirmPassword')} required error={errors.confirmPassword ? t('auth.passwordMismatch') : undefined}>
          <PasswordInput
            placeholder={t('auth.confirmPasswordPlaceholder')}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(v) => {
              setConfirmPassword(v)
              setValue('confirmPassword', v, { shouldValidate: true })
            }}
          />
        </PortalField>
        </fieldset>
        {error ? <p className="prod-error">{error}</p> : null}
        <button
          type="submit"
          className="prod-btn prod-btn--gold prod-btn--gold-full prod-auth__submit-primary"
          disabled={isSubmitting}
        >
          {t('auth.register')}
        </button>
        <div className="prod-auth__sticky-submit">
          <button
            type="submit"
            form="sign-up-form"
            className="prod-btn prod-btn--gold prod-btn--gold-full"
            disabled={isSubmitting}
          >
            {t('auth.register')}
          </button>
        </div>
        <p className="prod-auth__subtitle prod-auth__subtitle--after">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="prod-link">
            {t('auth.clickToSignIn')}
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}
